import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Button,
  Grid,
  Alert,
  Box,
  Chip,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  IconButton
} from '@mui/material';
import { 
  Refresh as RefreshIcon,
  Lock as LockIcon,
  Edit as EditIcon,
  AttachMoney as MoneyIcon
} from '@mui/icons-material';
import { useSettings } from '../../contexts/SettingsContext';
import { apiClient } from '../../services/api';

interface PricingSettingsData {
  services: Array<{
    id: string;
    name: string;
    category: string;
    price: number;
    duration: number;
  }>;
  discountCaps: Record<string, number>;
  priceGuardrails: Record<string, { min: number; max: number }>;
}

interface PricingSettingsProps {
  salonId: string;
}

const PricingSettings: React.FC<PricingSettingsProps> = ({ salonId }) => {
  const [settings, setSettings] = useState<PricingSettingsData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  const { checkAccess, getRestrictions } = useSettings();
  
  const canWrite = checkAccess('pricing', 'write');
  const restrictions = getRestrictions('pricing');

  useEffect(() => {
    fetchSettings();
  }, [salonId]);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiClient.get(`/settings/${salonId}/pricing`);
      
      if (response.success) {
        setSettings(response.data.settings);
      }
    } catch (err: any) {
      setError(err.response?.data?.error?.message || 'Failed to load pricing settings');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR'
    }).format(amount);
  };

  const getGuardrailStatus = (category: string, price: number) => {
    if (!settings?.priceGuardrails[category]) return null;
    
    const { min, max } = settings.priceGuardrails[category];
    
    if (price < min) return { status: 'below', message: `Below minimum (${formatCurrency(min)})` };
    if (price > max) return { status: 'above', message: `Above maximum (${formatCurrency(max)})` };
    return { status: 'within', message: 'Within limits' };
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!settings) {
    return (
      <Alert severity="error">
        Failed to load pricing settings. Please try refreshing the page.
      </Alert>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h6">Pricing & Service Management</Typography>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          {restrictions.length > 0 && (
            <Box sx={{ display: 'flex', gap: 1 }}>
              {restrictions.map(restriction => (
                <Chip
                  key={restriction}
                  icon={<LockIcon />}
                  label={restriction.replace(/_/g, ' ')}
                  color="warning"
                  size="small"
                />
              ))}
            </Box>
          )}
          <Button
            startIcon={<RefreshIcon />}
            onClick={fetchSettings}
            disabled={loading}
            size="small"
          >
            Refresh
          </Button>
        </Box>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Price Guardrails */}
        {Object.keys(settings.priceGuardrails).length > 0 && (
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Price Guardrails
                </Typography>
                <Alert severity="info" sx={{ mb: 2 }}>
                  These are the pricing limits set by Super Admin for different service categories.
                </Alert>
                
                <TableContainer component={Paper} variant="outlined">
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Category</TableCell>
                        <TableCell align="right">Minimum Price</TableCell>
                        <TableCell align="right">Maximum Price</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {Object.entries(settings.priceGuardrails).map(([category, limits]) => (
                        <TableRow key={category}>
                          <TableCell>
                            <Typography sx={{ textTransform: 'capitalize' }}>
                              {category.replace(/_/g, ' ')}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Chip 
                              label={formatCurrency(limits.min)} 
                              color="success" 
                              size="small" 
                            />
                          </TableCell>
                          <TableCell align="right">
                            <Chip 
                              label={formatCurrency(limits.max)} 
                              color="error" 
                              size="small" 
                            />
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Discount Caps */}
        {Object.keys(settings.discountCaps).length > 0 && (
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Discount Limits
                </Typography>
                <Alert severity="warning" sx={{ mb: 2 }}>
                  Maximum discount percentages allowed for different categories.
                </Alert>
                
                <Grid container spacing={2}>
                  {Object.entries(settings.discountCaps).map(([category, cap]) => (
                    <Grid item xs={12} sm={6} md={4} key={category}>
                      <Paper sx={{ p: 2, textAlign: 'center' }}>
                        <Typography variant="subtitle2" color="text.secondary">
                          {category.replace(/_/g, ' ').toUpperCase()}
                        </Typography>
                        <Typography variant="h4" color="warning.main">
                          {cap}%
                        </Typography>
                        <Typography variant="caption">
                          Maximum Discount
                        </Typography>
                      </Paper>
                    </Grid>
                  ))}
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Services List */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">
                  Services & Pricing
                </Typography>
                {!canWrite && (
                  <Chip 
                    icon={<LockIcon />}
                    label="Read Only" 
                    color="warning" 
                    size="small" 
                  />
                )}
              </Box>
              
              <TableContainer component={Paper} variant="outlined">
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Service Name</TableCell>
                      <TableCell>Category</TableCell>
                      <TableCell align="right">Duration (min)</TableCell>
                      <TableCell align="right">Price</TableCell>
                      <TableCell align="center">Status</TableCell>
                      <TableCell align="center">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {settings.services.map((service) => {
                      const guardrailStatus = getGuardrailStatus(service.category, service.price);
                      
                      return (
                        <TableRow key={service.id}>
                          <TableCell>
                            <Typography variant="body2">
                              {service.name}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip 
                              label={service.category} 
                              size="small" 
                              variant="outlined"
                            />
                          </TableCell>
                          <TableCell align="right">
                            {service.duration}
                          </TableCell>
                          <TableCell align="right">
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1 }}>
                              <MoneyIcon fontSize="small" color="action" />
                              <Typography variant="body2" fontWeight="medium">
                                {formatCurrency(service.price)}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell align="center">
                            {guardrailStatus && (
                              <Chip
                                label={guardrailStatus.status === 'within' ? 'OK' : guardrailStatus.status}
                                color={
                                  guardrailStatus.status === 'within' ? 'success' :
                                  guardrailStatus.status === 'above' ? 'error' : 'warning'
                                }
                                size="small"
                                title={guardrailStatus.message}
                              />
                            )}
                          </TableCell>
                          <TableCell align="center">
                            <IconButton 
                              size="small" 
                              disabled={!canWrite}
                              title="Edit service"
                            >
                              <EditIcon />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>

              {settings.services.length === 0 && (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography color="text.secondary">
                    No services configured yet. Add services to manage pricing.
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {restrictions.includes('within_guardrails') && (
        <Alert severity="info" sx={{ mt: 2 }}>
          <Typography variant="subtitle2" gutterBottom>
            Pricing Restrictions Active
          </Typography>
          <Typography variant="body2">
            All service prices must comply with the guardrails set by Super Admin. 
            Prices outside these limits will be automatically adjusted or rejected.
          </Typography>
        </Alert>
      )}
    </Box>
  );
};

export default PricingSettings;